import is from 'is';
import applianceClient from '@utils/graphql/creators/appliance';
import { getDevices } from '@utils/graphql/creators/devices';
import { IconBadge } from '@skyline-platform/alchemy';
import { getZones } from '@utils/graphql/creators/zones';
import { getSources } from '@utils/graphql/creators/sources';
import { getLogger } from '@utils/general';
import { useCallback, useRef } from 'react';
import { compareData } from '@utils/objectHandler';
import {
  parseEventMessage,
  determineLinkedFieldEventAction,
  determineEventAction,
  retrieveModelById,
} from './eventHelpers';
const logger = getLogger('src.pages.split.utils.helpers');

export const normalizeKeys = (obj) => {
  if (!obj) return {};

  const normalizedMetadata = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      normalizedMetadata[key.toLowerCase()] = obj[key];
    }
  }
  return normalizedMetadata;
};

export const formatObject = (obj) => {
  const formatted = JSON.parse(obj);
  let formattedString = '';
  for (const [key, value] of Object.entries(formatted)) {
    formattedString += `${key}: ${value} `;
  }
  return formattedString.trim();
};

// merges in updated zones, appliances, or sources
export const updateNestedField = async (field, updatedObject, tabDataRef) => {
  // finds nested object with matching id and merges it
  const id = updatedObject.id;

  const updatedTabRecord = await Promise.all(
    tabDataRef.current.map(async (obj) => {
      if (obj[field].id === id) {
        const updatedField = {
          ...obj[field],
          ...updatedObject,
        };

        // If updated object contains appliance id, update the source to an appliance and remove device
        if (
          !is.undefined(updatedObject.appliance_id) &&
          !is.null(updatedObject.appliance_id)
        ) {
          updatedField.device = null; // Clear device if appliance_id is present
          const { query, execute } = applianceClient._appliance(
            updatedObject.appliance_id
          );
          query.addReturnProperties('id', 'name');
          try {
            const { data } = await execute();
            updatedField.appliance = { name: data.name, id: data.id };
          } catch (error) {
            logger.error(error);
            throw new Error(error);
          }
        }
        // If updated object contains device id, update the source to a device and remove appliance
        if (
          !is.undefined(updatedObject.device_id) &&
          !is.null(updatedObject.device_id)
        ) {
          updatedField.appliance = null; // Clear appliance if device_id is present
          const { query, execute } = getDevices(updatedObject.device_id);
          query.addReturnProperties('id', 'name');
          try {
            const { data } = await execute();
            updatedField.device = { name: data.name, id: data.id };
          } catch (error) {
            logger.error(error);
            throw new Error(error);
          }
        }
        return {
          ...obj,
          [field]: updatedField,
        };
      }
      return obj; // Return original object if no match
    })
  );
  return updatedTabRecord;
};

/**
 * Updates the tab data array by checking for duplicates
 * @param {*} newTab  New tab object that may be added to the tab array
 *                    It must have an id property to be used for comparison
 * @param {*} setTabData The function to update the array of open tabs
 * @param {*} setFocusTabId The function to update the tab in focus
 * @returns {Object} Object containing isNewTab boolean and tabIndex number
 */
export const checkDuplicateTab = (newTab, setTabData, setFocusTabId) => {
  let result = { isNewTab: false, tabIndex: -1 };

  setTabData((prevTabs) => {
    // Check if the newStream already exists in the array
    const tabIndex = prevTabs.findIndex((tab) => tab.id === newTab.id);
    if (tabIndex !== -1) {
      setFocusTabId(newTab.id);
      result = { isNewTab: false, tabIndex };
      return [...prevTabs];
    }

    // If the newStream doesn't exist, just add it to the front
    const updatedTabData = [newTab, ...prevTabs];
    result = { isNewTab: true, tabIndex: 0 }; // New tab is always added at index 0
    return updatedTabData;
  });

  return result;
};

/**
 *
 * @param {*} index The index of the tab to be removed
 * @param {*} tabData The current array of all open tabs
 * @param {*} setTabData The function to update the array of open tabs
 * @param {*} tabEventEmitter Class containing different tab actions
 * @param {*} setCurrentFocusedTabId The tab that is in focus on the viewer side of the page
 */
export const removeTab = (index, tabData, setTabData, tabEventEmitter, setCurrentFocusedTabId) => {
  const chosenTabId = tabData[index].id;
  const currFocusedTabId =
    document.querySelector('.base--selected')?.firstChild.id;

  const isCurrentTab = chosenTabId === currFocusedTabId;

  setTabData((prevTabs) => {
    const updatedTabs = prevTabs.filter((_, tabIndex) => tabIndex !== index);

    if (updatedTabs.length <= 0) {
      tabEventEmitter.emit('selectTab', null); // remove selected on list side of page if no tabs
      return updatedTabs;
    }

    if (isCurrentTab) {
      const nextTabId =
        index >= prevTabs.length - 1
          ? updatedTabs[updatedTabs.length - 1].id
          : updatedTabs[index].id;

      tabEventEmitter.emit('selectTab', nextTabId);

      if (index >= prevTabs.length - 1) {
        const newLastTab = document.getElementById(nextTabId)?.parentElement;
        newLastTab.focus();
        newLastTab.click();
      }
    }

    return updatedTabs;
  });

  if (!isCurrentTab) {
    setCurrentFocusedTabId(currFocusedTabId); // Store the ID for the next useEffect
  }
};

// @todo: consolidate the badges in Manager or make a reusable component
export const renderBadgeForCameraStatus = (offlineStreams) => {
  const online = offlineStreams > 0 ? false : true;

  return (
    <IconBadge
      label={`Camera ${online ? 'Online' : 'Offline Count'}`}
      iconClassName={online ? 'fa-solid fa-video' : 'fa-light fa-video-slash'}
      srText={online ? 'Camera Online' : 'Camera Offline Count'}
      badge={online ? undefined : offlineStreams}
      badgeColor='var(--vp-secondary-orange)'
      badgeTextColor='var(--vp-secondary-black)'
      max={50}
      className='camera-status-badge'
    />
  );
};

export const renderBadgeForStreamStatus = (onlineStatus) => (
  <IconBadge
    label={`Stream ${onlineStatus ? 'Online' : 'Offline'}`}
    iconClassName={onlineStatus ? 'fa-solid fa-video' : 'fa-light fa-video-slash'}
    srText={onlineStatus ? 'Stream Online' : 'Stream Offline'}
    //add badge numbers in later story
    badge={undefined}
    badgeColor='--vp-secondary-orange'
    badgeTextColor='var(--vp-white)'
    max={0}
    className='stream-status-badge'
  />
);

export const renderBadgeForPermission = (permission, permissionName) => (
  <IconBadge
    label={`${permission ? 'Can' : 'Cannot'} ${permissionName}`}
    iconClassName={permission ? 'fa-solid fa-check' : 'fa-solid fa-xmark'}
    iconStyle={{ color: permission ? 'green' : 'red' }}
    srText={permission ? 'Yes' : 'No'}
    className='permission-badge'


  />
);

/**
 * This updates an object within an object many layers deep
 * Ex: Camera object --> streams array --> item in array --> object within that first item
 * @param {*} field
 * @param {*} updatedObject
 * @param {*} tabDataRef
 * @param {*} callback
 */
export const updateNestedCameraObject = (field, updatedObject, tabDataRef, callback) => {
  // Helper function to find and update nested objects
  function updateNestedArray(array, update, idKey) {
    return array.map((item) => {
      if (item[idKey] === update[idKey]) {
        let newItem = { ...item };
        for (let key in update) {
          if (Object.prototype.hasOwnProperty.call(newItem, key)) {
            newItem[key] = update[key];
          }
        }
        return newItem;
      }
      return item;
    });
  }

  // Helper function to fetch and update zone data
  const fetchAndUpdateZone = async (item, update) => {
    const zoneExists = item.streams.some((stream) => stream.zone && stream.zone.id === update.previous.zone_id);
    if (zoneExists) {
      const { query, execute } = getZones(update.current.zone_id);
      query.addReturnProperties('id', 'name');
      try {
        const newZoneData = await execute();
        return item.streams.map((stream) => {
          if (stream.zone
            && stream.id === update.current.id
            && stream.zone.id === update.previous.zone_id) {
            let newZone = { ...stream.zone };
            // Only replace the values that are already present
            for (let key in newZoneData.data) {
              if (Object.prototype.hasOwnProperty.call(newZone, key)) {
                newZone[key] = newZoneData.data[key];
              }
            }
            return { ...stream, zone: newZone };
          }
          return stream;
        });
      } catch (error) {
        logger.error(error);
        throw new Error(error);
      }
    }
    return item.streams;
  };

  // Helper function to fetch and update appliance data
  const fetchAndUpdateAppliance = async (item, update) => {
    const applianceExists =
      item.streams.some((stream) => stream.appliance && stream.appliance.id === update.previous.appliance_id);
    if (applianceExists) {
      const { query, execute } = applianceClient._appliance(update.current.appliance_id);
      query.addReturnProperties('id', 'name', 'public_address', 'management_address');
      try {
        const newApplianceData = await execute();
        return item.streams.map((stream) => {
          if (stream.appliance
            && stream.id === update.current.id
            && stream.appliance.id === update.previous.appliance_id) {
            let newAppliance = { ...stream.appliance };
            // Only replace the values that are already present
            for (let key in newApplianceData.data) {
              if (Object.prototype.hasOwnProperty.call(newAppliance, key)) {
                newAppliance[key] = newApplianceData.data[key];
              }
            }
            return { ...stream, appliance: newAppliance };
          }
          return stream;
        });
      } catch (error) {
        logger.error(error);
        throw new Error(error);
      }
    }
    return item.streams;
  };

  // Helper function to update nested fields
  const updateFields = async (item, fieldName, update) => {

    let newItem = { ...item };
    if (fieldName === 'stream') {
      if (update.current && update.current.id) {
        newItem.streams = updateNestedArray(item.streams, update.current, 'id');
      }
      if (update.current && update.previous) {
        if (update.current.zone_id !== update.previous.zone_id) {
          newItem.streams = await fetchAndUpdateZone(newItem, update);
        }
        if (update.current.appliance_id !== update.previous.appliance_id) {
          newItem.streams = await fetchAndUpdateAppliance(newItem, update);
        }
      }
    } else if (fieldName === 'source') {
      if (!is.null(update.current) && update.current.id) {
        newItem.sources = updateNestedArray(item.sources, update.current, 'id');
      }
    } else if (fieldName === 'zone' && update.current.id) {
      newItem.streams = item.streams.map((stream) => {
        if (stream.zone && stream.zone.id === update.current.id) {
          let newStream = { ...stream, zone: { ...stream.zone } };
          for (let key in update.current) {
            if (Object.prototype.hasOwnProperty.call(newStream.zone, key)) {
              newStream.zone[key] = update.current[key];
            }
          }
          return newStream;
        }
        return stream;
      });
    } else if (fieldName === 'appliance' && update.current.id) {
      newItem.streams = item.streams.map((stream) => {
        if (stream.appliance && stream.appliance.id === update.current.id) {
          let newStream = { ...stream, appliance: { ...stream.appliance } };
          for (let key in update.current) {
            if (Object.prototype.hasOwnProperty.call(newStream.appliance, key)) {
              newStream.appliance[key] = update.current[key];
            }
          }
          return newStream;
        }
        return stream;
      });
    }
    return newItem;
  };

  // Iterate over the array of objects and create a new array
  Promise.all(tabDataRef.current.map((item) => updateFields(item, field, updatedObject)))
    .then((updatedArray) => callback(updatedArray))
    .catch((error) => logger.error(error));
};

/**
 * Fallback function to check a newly created source for the current tab id (ie device id)
 * in case the source was not properly updated in the tab data.
 * @param {*} sourceId
 * @param {*} tabDataRef
 * @returns
 */
export const checkSourceForDevice = (sourceId, tabDataRef) => {
  const { query, execute } = getSources(sourceId);
  query.addReturnProperties('name', 'id', 'device.id', 'device.name');

  return execute()
    .then((res) => {
      return tabDataRef.current.some((tab) =>
        tab.id === res.data.device.id
      );
    })
    .catch((error) => {
      logger.error(error.message);
    });
};

/**
 * Handles keyboard navigation and actions within the table actions menu.
 *
 * This function allows navigation between buttons in a table menu using
 * the `ArrowUp` and `ArrowDown` keys, and triggers the button click action
 * with the `Enter` or `Space` keys. It ensures Escape and Tab keypresses
 * are allowed to propagate so the menu can close naturally.
 *
 * @param {KeyboardEvent} event - The keyboard event triggered by the user.
 * @param {Array<Object>} btnArray - Array of button references (React refs) to navigate through.
 * @param {number} currentBtnIndex - The current index of the button the key event came from in the `btnArray`.
 *
 * @returns {void}
 *
 */
export const handleTableActionsKeyDown = (event, btnArray, currentBtnIndex) => {
  // Allow Escape and Tab to bubble so the menu can close
  if (event.key === 'Escape' || event.key === 'Tab') {
    return;
  }
  event.stopPropagation();
  event.preventDefault();

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    btnArray[currentBtnIndex].current?.click();
  }

  if (event.key === 'ArrowDown') {
    if (currentBtnIndex === btnArray.length - 1) {
      btnArray[0].current.focus();
    } else {
      btnArray[currentBtnIndex + 1].current.focus();
    }
  }

  if (event.key === 'ArrowUp') {
    if (currentBtnIndex === 0) {
      btnArray[btnArray.length - 1].current.focus();
    } else {
      btnArray[currentBtnIndex - 1].current.focus();
    }
  }
};

/**
 * Function to keep original order of streams from device when updating or adding
 * new streams to a device
 * @param {*} initialDevice original device with original order
 * @param {*} updatedDevice updated device with bad order
 * @returns
 */
export const updateStreamOrder = (initialDevice, updatedDevice) => {
  const originalOrder = initialDevice.streams.map((stream) => stream.id);
  const updatedStreams = updatedDevice.streams;

  const orderedStreams = [...updatedStreams].sort((a, b) => {
    const indexA = originalOrder.indexOf(a.id);
    const indexB = originalOrder.indexOf(b.id);

    if (indexA === -1 && indexB === -1) {
      // Both are new, sort by name
      return a.name.localeCompare(b.name);
    }

    if (indexA === -1) return 1; // a is new, move it after known ones
    if (indexB === -1) return -1; // b is new, move it after known ones

    return indexA - indexB;
  });

  return {
    ...updatedDevice,
    streams: orderedStreams,
  };
};

/**
 * Debounces an async function and returns a debounced version that returns a Promise.
 * The promise resolves when the async function completes after the debounce delay.
 *
 * @param {Function} asyncFn - The async function to debounce
 * @param {number} delay - Debounce delay in ms
 */
export const useDebouncedAsync = (asyncFn, delay) => {
  const timeoutRef = useRef(null);

  const debounced = useCallback((...args) => {
    // Clear any existing debounce timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Return a Promise that resolves/rejects once the asyncFn completes
    return new Promise((resolve, reject) => {
      timeoutRef.current = setTimeout(async () => {
        try {
          const result = await asyncFn(...args);
          resolve(result);
        } catch (err) {
          reject(err);
        }
      }, delay);
    });
  }, [asyncFn, delay]);

  return debounced;
};

/**
 * Indicates if a stream's address has changed.
 *
 * This can be the result of:
 * - the stream name changes
 * - the appliance the stream is attached to has changed
 * - the appliance the stream is attached to has changed public addresses
 * - the appliance the stream is attached to has change management addresses while the public address is null
 *
 * @param {object} currentStream The current object information for a stream.
 * @param {object} updatedStream The new object information for the same stream.
 * @returns boolean
 */
export const streamAddressHasChanged = (currentStream, updatedStream) => {
  const streamChanges = Object.keys(compareData(currentStream, updatedStream));
  if (streamChanges.includes('name')) {
    return true;
  }

  try {
    const applianceChanges = Object.keys(compareData(currentStream.appliance, updatedStream.appliance));

    if (applianceChanges.includes('id')) {
      return true;
    }

    if (applianceChanges.includes('public_address')) {
      return true;
    }

    if (applianceChanges.includes('management_address')) {
      return !updatedStream.appliance?.public_address;
    }
  } catch {
    // do nothing; just fall through
  }

  return false;
};

/**
 * Patches specific fields on a single table row in-place without re-rendering unaffected rows.
 *
 * @param {string} id The id of the row to patch.
 * @param {object} rowPatch Key/value pairs of display fields to apply to the row.
 * @param {{ rowsRef: React.MutableRefObject, setRows: Function }} context
 *   Table state context.
 * @param {object|null} [actionPatch=null] Key/value pairs to apply specifically to row.action.
 *   Use this when display fields (e.g. zone: 'Name') differ from the action object fields
 *   (e.g. action.zone: { id, name }). Defaults to rowPatch when not provided.
 */
export const patchRow = (id, rowPatch, context, actionPatch = null) => {
  const {
    rowsRef,
    setRows,
  } = context;

  const idx = rowsRef.current?.findIndex((r) => String(r.id) === String(id));
  if (idx === -1 || idx === undefined) return;

  const patchedRow = {
    ...rowsRef.current[idx],
    ...rowPatch,
    action: { ...rowsRef.current[idx].action, ...(actionPatch ?? rowPatch) },
  };

  const newRows = [...rowsRef.current];
  newRows[idx] = patchedRow;
  rowsRef.current = newRows;
  setRows(newRows);
};

/**
 * Removes a single row from the table by id without re-rendering unaffected rows.
 *
 * Also cleans up selection state for the removed row.
 *
 * @param {string} id The id of the row to remove.
 * @param {{ rowsRef: React.MutableRefObject, setRows: Function,
 *  setSelectedRowIds: Function, setSelectedId: Function }} context Table state context.
 */
export const removeRow = (id, context) => {
  const {
    rowsRef,
    setRows,
    setSelectedRowIds: setSelectedIds,
    setSelectedId,
  } = context;

  const newRows = rowsRef.current.filter((r) => String(r.id) !== String(id));
  rowsRef.current = newRows;
  setRows([...newRows]);
  setSelectedIds((prev) => prev.filter((rowId) => String(rowId) !== String(id)));
  setSelectedId((prev) => (String(prev) === String(id) ? undefined : prev));
};

/**
 * Adds a single formatted row to the table without re-rendering unaffected rows.
 *
 * Skips the add if a row with the same id already exists (dedup guard).
 *
 * @param {object} item Raw model object to add.
 * @param {Function} formatRow Function that maps a raw item to a table row (e.g. mapTableData).
 * @param {{ rowsRef: React.MutableRefObject, setRows: Function }} context Table state context.
 */
export const addRow = (item, formatRow, context) => {
  const {
    rowsRef,
    setRows,
  } = context;

  if (rowsRef.current?.some((r) => String(r.id) === String(item.id))) return;
  const [formattedRow] = formatRow([item]);
  rowsRef.current = [...rowsRef.current, formattedRow];
  setRows([...rowsRef.current]);
};

const STREAM_NESTED_ID_GETTERS = {
  zone: (row) => row.action?.zone?.id,
  appliance: (row) => row.action?.appliance?.id,
  source: (row) => row.action?.source?.id,
};

const STREAM_NESTED_PATCH_BUILDERS = {
  zone: (data) => data?.name !== undefined ? { zone: data.name } : {},
  appliance: (data) => data?.name !== undefined ? { appliance: data.name } : {},
  source: (data) => data?.name !== undefined ? { source: data.name } : {},
};

/**
 * Applies a nested entity update (zone, appliance, or source) to all matching stream table rows.
 *
 * Reads the updated name directly from the event payload (data.current) — no API call needed.
 * Returns a new rows array with the patch applied, or null if the field is unknown or produces no change.
 *
 * @param {object[]} rows Current array of stream table rows.
 * @param {string} nestedUpdatedField The linked field name ('zone_id', 'appliance_id', 'source_id').
 * @param {string} nestedUpdatedId The id of the updated entity.
 * @param {object} nestedUpdatedData The updated entity object from the event payload (data.current).
 * @returns {object[]|null} Updated rows array, or null if no valid patch could be built.
 */
export const applyStreamNestedPatch = (rows, nestedUpdatedField, nestedUpdatedId, nestedUpdatedData) => {
  const getNestedId = STREAM_NESTED_ID_GETTERS[nestedUpdatedField];
  const buildPatch = STREAM_NESTED_PATCH_BUILDERS[nestedUpdatedField];
  if (!getNestedId || !buildPatch || !Array.isArray(rows)) return null;
  const patch = buildPatch(nestedUpdatedData);
  if (Object.keys(patch).length === 0) return null;
  return rows.map((row) =>
    getNestedId(row) === nestedUpdatedId ? { ...row, ...patch } : row
  );
};

/**
 * Fetches a single parent row by id and patches it in the table.
 *
 * Used when a linked entity update can't be applied in-place (e.g. the linked entity
 * is embedded in an array on the parent row). The fresh fetch reflects any backend
 * changes so derived values like stream counts stay accurate.
 *
 * @param {string|null} parentId Id of the parent row to fetch and patch. No-ops if falsy.
 * @param {string} sourceModel Model type to fetch ('stream' or 'device').
 * @param {string[]} fetchFields Fields to include in the fetch request.
 * @param {Function} mapTableData Maps the fetched model to a table row.
 * @param {object} tableContext Table state context ({ rowsRef, setRows, setFilteredData, ... }).
 */
async function fetchAndPatchParentRowById(parentId, sourceModel, fetchFields, mapTableData, tableContext) {
  if (!parentId) return;
  const fetched = await retrieveModelById(sourceModel, parentId, fetchFields);
  if (fetched) {
    const [mappedRow] = mapTableData([fetched]);
    patchRow(parentId, mappedRow, tableContext);
  }
}

/**
 * Generic SSOT event handler for list-view table pages (streams, cameras, etc.).
 *
 * Handles two event categories:
 * - Linked-field events: the event model differs from sourceModel (e.g. a zone update
 *   affecting stream rows). Applies a shallow patch via applyStreamNestedPatch, or falls
 *   back to fetching and re-mapping the parent row via resolveParentId if the linked entity
 *   is array-embedded (e.g. streams/sources on a camera row).
 * - Direct events: the event model matches sourceModel. Handles create (fetch + addRow),
 *   remove (removeRow), and update (patchRow, or fetch + patchRow if linked id fields changed).
 *
 * @param {object} eventMessage Raw event from the message emitter.
 * @param {object} options
 * @param {string} options.sourceModel The model this table represents ('stream' or 'device').
 * @param {string[]} options.fetchFields Fields to request when fetching a full model by id.
 * @param {Function} options.mapTableData Maps raw model objects to table rows.
 * @param {object} options.tableContext Table state context ({ rowsRef, setRows, ... }).
 * @param {Function} options.applyRows Applies a full updated rows array (used for linked-field patches).
 * @param {React.MutableRefObject<Map>} options.nestedUpdateTimersRef Per-entity debounce timers for linked events.
 * @param {Function} [options.buildFetchPatch] Returns { rowPatch, actionPatch } from a fetched model.
 *   Use this to map nested objects to display strings (rowPatch) while preserving full objects in
 *   action (actionPatch) for future id lookups. Defaults to returning empty patches.
 * @param {Function} [options.resolveParentId] (model, linkedEntityId, rows) => parentRowId|null.
 *   Required when linked entities are stored as arrays on the parent row (e.g. camera.streams).
 *   Used to find the parent row id when applyStreamNestedPatch produces no direct match.
 */
export const handleTableEvent = async (
  eventMessage,
  {
    sourceModel,
    fetchFields,
    mapTableData,
    tableContext,
    applyRows,
    nestedUpdateTimersRef,
    buildFetchPatch = () => ({ rowPatch: {}, actionPatch: {} }),
    resolveParentId = null,
  }
) => {
  const parsedEventMessage = parseEventMessage(eventMessage);
  if (!parsedEventMessage) return;

  const { model } = parsedEventMessage;
  const isLinkedFieldEvent = model !== sourceModel;

  if (isLinkedFieldEvent) {
    const { valuesToUpdate, updatedId } = determineLinkedFieldEventAction(sourceModel, parsedEventMessage);

    if (!updatedId) return;

    const debounceKey = `${model}:${updatedId}`;
    if (nestedUpdateTimersRef.current.has(debounceKey)) {
      clearTimeout(nestedUpdateTimersRef.current.get(debounceKey));
    }

    nestedUpdateTimersRef.current.set(debounceKey, setTimeout(async () => {
      nestedUpdateTimersRef.current.delete(debounceKey);
      const updatedRows = applyStreamNestedPatch(
        tableContext.rowsRef.current,
        model,
        updatedId,
        valuesToUpdate
      );
      if (updatedRows) {
        applyRows(updatedRows);
      } else if (resolveParentId) {
        const parentId = resolveParentId(model, updatedId, tableContext.rowsRef.current);
        await fetchAndPatchParentRowById(parentId, sourceModel, fetchFields, mapTableData, tableContext);
      }
    }, 200));
    return;
  }

  const {
    fetchNeeded,
    valuesToUpdate,
    removedId,
    createdId,
    updatedId,
  } = determineEventAction(parsedEventMessage);

  if (removedId) {
    removeRow(removedId, tableContext);
    return;
  }

  if (createdId) {
    const created = await retrieveModelById(sourceModel, createdId, fetchFields);
    if (created) addRow(created, mapTableData, tableContext);
    return;
  }

  if (!fetchNeeded) {
    patchRow(updatedId, valuesToUpdate, tableContext);
  } else {
    const fetched = await retrieveModelById(sourceModel, updatedId, fetchFields);
    if (!fetched) return;
    const { rowPatch: fetchRowPatch = {}, actionPatch: fetchActionPatch = {} } = buildFetchPatch(fetched);
    const rowPatch = { ...valuesToUpdate, ...fetchRowPatch };
    const actionPatch = { ...valuesToUpdate, ...fetchActionPatch };
    patchRow(updatedId, rowPatch, tableContext, actionPatch);
  }
};
